import { useMemo, useState } from "react";
import styles from "./EmailsList.module.scss";
import { Button } from "@/components";
import type { GmailMessageListItem } from "../../_types/gmail.types";
import { EmailListItem } from "../email-list-item";
import { EmailDetailsDrawer } from "../email-details-drawer";

type EmailsListProps = {
  items: GmailMessageListItem[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  /** Optional-env integration missing (e.g. gmail_not_configured) — controlled unavailable, not a transient error. */
  unavailable?: boolean;
  errorMessage: string | null;
  onRetry: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
};

export const EmailsList = ({
  items,
  isLoading,
  isFetching,
  isError,
  unavailable = false,
  errorMessage,
  onRetry,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: EmailsListProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selectedSummary = useMemo(
    () => items.find((m) => m.id === selectedId) ?? null,
    [items, selectedId],
  );

  if (isLoading && !unavailable) {
    return (
      <div className={styles["comp__state"]}>
        <div className={styles["comp__spinner"]} />
        <div>Loading messages...</div>
      </div>
    );
  }

  if (unavailable) {
    return (
      <div className={styles["comp__state"]}>
        <div>{errorMessage ?? "Email inbox is unavailable in this environment."}</div>
        <p>
          Configure Gmail OAuth credentials on the Worker to enable this inbox.
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles["comp__state"]}>
        <div>{errorMessage ?? "Failed to load messages."}</div>
        <Button size="small" variant="secondary" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }

  if (items.length === 0) {
    return <div className={styles["comp__state"]}>No messages found.</div>;
  }

  return (
    <>
      <div className={styles["comp"]} aria-busy={isFetching}>
        <div className={styles["comp__table-header"]}>
          <div className={styles["comp__column"]}>From</div>
          <div className={styles["comp__column"]}>Subject</div>
          <div className={styles["comp__column"]}>Preview</div>
          <div className={styles["comp__column"]}>Date</div>
        </div>

        <div className={styles["comp__list"]}>
          {items.map((message) => (
            <EmailListItem
              key={message.id}
              message={message}
              onClick={() => setSelectedId(message.id)}
            />
          ))}
        </div>

        {hasNextPage ? (
          <div className={styles["comp__load-more"]}>
            <Button
              size="small"
              variant="secondary"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          </div>
        ) : null}
      </div>

      <EmailDetailsDrawer
        messageId={selectedId}
        summary={selectedSummary}
        isOpen={selectedId !== null}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
};
